import {
    SharedUiBackgroundColorName,
    type SharedUiBackgroundColorOptions,
    type SharedUiColoredBackgroundColorOptions,
    SharedUiColorName,
    SharedUiColorsService,
    SharedUiTextColorDerivation
} from '@/shared/ui/theming';
import { FeatureCode } from '@/utils/disabled-features/disabled-features.interface';
import { DisabledFeaturesService } from '@/utils/disabled-features/disabled-features.service';
import type { LiteralValues } from '@/utils/typing/literal-values.type';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, Renderer2 } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { of, switchMap } from 'rxjs';
import { AdvizeoUiElementSize } from '../../interfaces/AdvizeoUi.interface';

export const IconStyle = {
    REGULAR: 'regular',
    FILL: 'fill',
    DUOTONE: 'duotone',
    BOLD: 'bold'
} as const;
export type IconStyle = LiteralValues<typeof IconStyle>;

export const IconName = {
    CREATE: 'create',
    ADD: 'add',
    EDIT: 'edit',
    CONFIGURE: 'configure',
    DELETE: 'delete',
    REMOVE: 'remove',
    SAVE: 'save',
    OPEN: 'open',
    CONVERT: 'convert',

    DOWNLOAD: 'download',
    EXPORT_SPREADSHEET: 'export-spreadsheet',
    PRINT: 'print',
    SELECT_FILE: 'select-file',
    DOWNLOADED: 'downloaded',

    EXPORT: 'export',
    IMPORT: 'import',

    YES: 'yes',

    EXPAND: 'expand',
    RETRACT: 'retract',
    EXPAND_SIDE: 'expand-side',
    RETRACT_SIDE: 'retract-side',

    PREVIOUS: 'previous',
    NEXT: 'next',

    UP: 'up',
    GO_TO_FEATURE: 'go-to-feature',
    DOWN: 'down',
    LEFT: 'left',
    RIGHT: 'right',

    // positivity
    POSITIVE: 'positive',
    NEUTRAL: 'neutral',
    NEGATIVE: 'negative',

    // status
    SUCCESS: 'success',
    FAILURE: 'failure',

    // rating
    RATING_VERY_GOOD: 'rating-very-good',
    RATING_GOOD: 'rating-good',
    RATING_MEDIUM: 'rating-medium',
    RATING_BAD: 'rating-bad',
    RATING_VERY_BAD: 'rating-very-bad',

    LATE: 'late',

    FAVORITE: 'favorite',

    INFO: 'info',
    WARNING: 'warning',

    VISIBLE: 'visible',
    HIDDEN: 'hidden',
    LOCKED: 'locked',
    UNLOCKED: 'unlocked',
    NONE: 'none',

    LIST: 'list',
    FOLDER: 'folder',
    HISTORY: 'history',
    HELP: 'help',
    DATE: 'date',
    SEND_MESSAGE: 'send-message',
    MENU: 'menu',
    DROPDOWN_MENU: 'dropdown-menu',
    PANEL_SELECTOR: 'panel-selector',
    MORE: 'more',
    CALL: 'call',
    SHARE: 'share',
    TARGET: 'target',
    AUTOMATIC: 'automatic',
    MISC_DATA: 'misc-data',

    // Left menu v2
    BUILDINGS: 'buildings',
    HOME_LIGHT: 'home_light',
    IMPORT_AND_EXPORT: 'import_or_export',
    SUPPORT_LIGHT: 'support_light',
    ADMIN: 'admin',

    // Entities
    BUILDING: 'building',
    CLASSIFICATION: 'classification',
    DELIVERY_POINT: 'delivery-point',
    SYSTEM: 'system',
    ACTION: 'action',
    INVOICE: 'invoice',
    SUPPLIER: 'supplier',
    PROJECT: 'project',
    METER: 'meter',
    REPORT: 'report',
    ACCESSES: 'accesses',
    USER: 'user',
    USERS: 'users',
    PROFILE: 'profile',
    PROFILES: 'profiles',
    ATTRIBUTE: 'attribute',
    ALERT: 'alert',
    DASHBOARD: 'dashboard',
    FUNCTION: 'function',
    WIRELESS: 'wireless',

    // Features
    HOME: 'home',
    FEED: 'feed',
    PICTURE: 'picture',
    MAPS: 'maps',
    SUPPORT: 'support',
    INDICATORS_AND_OBJECTIVES: 'indicator-and-objectives',
    MANUAL_READINGS: 'manual-readings',
    IPMVP: 'ipmvp',
    TYPICAL_WEEKS: 'typical-weeks',

    // Feature categories
    TRACK: 'track',
    STEER: 'steer',
    ANALYZE: 'analyze',

    NEW: 'new',

    LOG_IN: 'log-in',
    LOG_OUT: 'log-out',
    LOG_AS: 'log-as',

    CANCEL: 'cancel',
    CLOSE_LEFT: 'close-left',
    REFRESH: 'refresh',
    VALIDATE: 'validate',
    FILTER: 'filter',
    SEARCH: 'search',
    COPY: 'copy',
    DUPLICATE: 'duplicate',
    VOTE: 'vote',

    FROM_TO: 'from-to',
    GO_TO: 'go-to',
    GO_BACK: 'go-back',
    PARENT: 'parent',

    RELEASE_NOTES: 'release-notes',

    LOADING: 'loading',

    ANY_CHART: 'any-chart',
    BAR_CHART: 'bar-chart',
    COLUMN_CHART: 'column-chart',
    LINE_CHART: 'line-chart',
    COMBO_CHART: 'combo-chart',
    PIE_CHART: 'pie-chart',
    SCATTER_CHART: 'scatter-chart',
    TABLE: 'table',
    PIVOT_TABLE: 'pivot-table',
    INDEX_CHART: 'index-chart',
    HEATMAP: 'heatmap',

    SINGLE_VALUE: 'single-value',
    SURFACE: 'surface',

    // Quick Actions
    QUICK_ACTION: 'quick-action',
    SHORTCUT: 'shortcut',

    // value evolution
    INCREASE: 'increase',
    DECREASE: 'decrease',
    STABLE: 'stable',
    TOP: 'top'
} as const;
export type IconName = LiteralValues<typeof IconName>;

const fontAwesomeIconMap: Record<IconName, { set: string; icon: string; classes?: Record<string, boolean> }> = {
    [IconName.CREATE]: { set: 'fa-solid', icon: 'fa-plus' },
    [IconName.ADD]: { set: 'fa-solid', icon: 'fa-plus' },
    [IconName.EDIT]: { set: 'fa-solid', icon: 'fa-pencil' },
    [IconName.CONFIGURE]: { set: 'fa-solid', icon: 'fa-cog' },
    [IconName.DELETE]: { set: 'fa-solid', icon: 'fa-trash' },
    [IconName.SAVE]: { set: 'fa-solid', icon: 'fa-save' },
    [IconName.CONVERT]: { set: 'fa-solid', icon: 'fa-arrow-up-arrow-down' },
    [IconName.OPEN]: { set: 'fa-solid', icon: 'fa-folder-open' },
    [IconName.REMOVE]: { set: 'fa-solid', icon: 'fa-times' },
    [IconName.DOWNLOAD]: { set: 'fa-solid', icon: 'fa-download' },
    [IconName.DOWNLOADED]: { set: 'fa-solid', icon: 'fa-check-to-slot' },
    [IconName.PRINT]: { set: 'fa-solid', icon: 'fa-print' },
    [IconName.SELECT_FILE]: { set: 'fa-solid', icon: 'fa-folder-open' },
    [IconName.LIST]: { set: 'fa-solid', icon: 'fa-list' },
    [IconName.FOLDER]: { set: 'fa-solid', icon: 'fa-folder' },
    [IconName.HISTORY]: { set: 'fa-solid', icon: 'fa-history' },
    [IconName.VISIBLE]: { set: 'fa-solid', icon: 'fa-eye' },
    [IconName.HIDDEN]: { set: 'fa-solid', icon: 'fa-eye-slash' },
    [IconName.LOCKED]: { set: 'fa-solid', icon: 'fa-lock' },
    [IconName.UNLOCKED]: { set: 'fa-solid', icon: 'fa-unlock-alt' },
    [IconName.NONE]: { set: 'fa-solid', icon: 'fa-empty-set' },
    [IconName.YES]: { set: 'fa-solid', icon: 'fa-check-circle' },
    [IconName.EXPAND]: { set: 'fa-solid', icon: 'fa-angle-right', classes: { 'animate-transform': true, 'rot-0': true } },
    [IconName.RETRACT]: { set: 'fa-solid', icon: 'fa-angle-right', classes: { 'animate-transform': true, 'rot-90': true } },
    [IconName.EXPAND_SIDE]: { set: 'fa-solid', icon: 'fa-angle-down', classes: { 'animate-transform': true, 'rot-0': true } },
    [IconName.RETRACT_SIDE]: { set: 'fa-solid', icon: 'fa-angle-down', classes: { 'animate-transform': true, 'rot-minus90': true } },
    [IconName.PREVIOUS]: { set: 'fa-solid', icon: 'fa-angle-left' },
    [IconName.NEXT]: { set: 'fa-solid', icon: 'fa-angle-right' },
    [IconName.UP]: { set: 'fa-solid', icon: 'fa-arrow-up' },
    [IconName.DOWN]: { set: 'fa-solid', icon: 'fa-arrow-down' },
    [IconName.POSITIVE]: { set: 'fa-solid', icon: 'fa-smile' },
    [IconName.NEUTRAL]: { set: 'fa-solid', icon: 'fa-meh' },
    [IconName.NEGATIVE]: { set: 'fa-solid', icon: 'fa-frown' },
    [IconName.SUCCESS]: { set: 'fa-solid', icon: 'fa-thumbs-up' },
    [IconName.FAILURE]: { set: 'fa-solid', icon: 'fa-thumbs-down' },
    [IconName.RATING_VERY_GOOD]: { set: 'fa-solid', icon: 'fa-smile' },
    [IconName.RATING_GOOD]: { set: 'fa-solid', icon: 'fa-smile' },
    [IconName.RATING_MEDIUM]: { set: 'fa-solid', icon: 'fa-meh' },
    [IconName.RATING_BAD]: { set: 'fa-solid', icon: 'fa-frown' },
    [IconName.RATING_VERY_BAD]: { set: 'fa-solid', icon: 'fa-frown' },
    [IconName.LATE]: { set: 'fa-solid', icon: 'fa-clock' },
    [IconName.FAVORITE]: { set: 'fa-solid', icon: 'fa-star' },
    [IconName.HELP]: { set: 'fa-regular', icon: 'fa-question-circle' },
    [IconName.INFO]: { set: 'fa-solid', icon: 'fa-info-circle' },
    [IconName.EXPORT_SPREADSHEET]: { set: 'fa-solid', icon: 'fa-file-excel' },
    [IconName.DATE]: { set: 'fa-solid', icon: 'fa-calendar-days' },
    [IconName.WARNING]: { set: 'fa-solid', icon: 'fa-exclamation-triangle' },
    [IconName.SEND_MESSAGE]: { set: 'fa-solid', icon: 'fa-envelope' },
    [IconName.MENU]: { set: 'fa-solid', icon: 'fa-bars' },
    [IconName.DROPDOWN_MENU]: { set: 'fa-solid', icon: 'fa-caret-down' },
    [IconName.PANEL_SELECTOR]: { set: 'fa-solid', icon: 'fa-grid' },
    [IconName.MORE]: { set: 'fa-solid', icon: 'fa-ellipsis-v' },
    [IconName.CALL]: { set: 'fa-solid', icon: 'fa-phone' },
    [IconName.SHARE]: { set: 'fa-solid', icon: 'fa-share-from-square' },
    [IconName.TARGET]: { set: 'fa-solid', icon: 'fa-bullseye-arrow' },
    [IconName.AUTOMATIC]: { set: 'fa-solid', icon: 'fa-wand-sparkles' },
    [IconName.MISC_DATA]: { set: 'fa-solid', icon: 'fa-database' },
    [IconName.HOME_LIGHT]: { set: 'fa-light', icon: 'fa-home' },
    [IconName.IMPORT_AND_EXPORT]: { set: 'fa-light', icon: 'fa-down-to-bracket' },
    [IconName.SUPPORT_LIGHT]: { set: 'fa-light', icon: 'fa-headset' },
    [IconName.ADMIN]: { set: 'fa-light', icon: 'fa-user-gear' },
    [IconName.BUILDING]: { set: 'fa-solid', icon: 'fa-building' },
    [IconName.BUILDINGS]: { set: 'fa-light', icon: 'fa-buildings' },
    [IconName.CLASSIFICATION]: { set: 'fa-solid', icon: 'fa-sitemap' },
    [IconName.DELIVERY_POINT]: { set: 'fa-solid', icon: 'fa-meter' },
    [IconName.SYSTEM]: { set: 'fa-solid', icon: 'fa-router' },
    [IconName.WIRELESS]: { set: 'fa-solid', icon: 'fa-wifi' },
    [IconName.ALERT]: { set: 'fa-solid', icon: 'fa-bell' },
    [IconName.DASHBOARD]: { set: 'fa-regular', icon: 'fa-grid-2' },
    [IconName.METER]: { set: 'fa-solid', icon: 'fa-arrows-to-dot' },
    [IconName.ACTION]: { set: 'fa-solid', icon: 'fa-clipboard-list-check' },
    [IconName.INVOICE]: { set: 'fa-solid', icon: 'fa-file-invoice-dollar' },
    [IconName.SUPPLIER]: { set: 'fa-solid', icon: 'fa-pipe-valve' },
    [IconName.PROJECT]: {
        set: 'fa-solid',
        icon: 'fa-layer-group'
    },
    [IconName.REPORT]: { set: 'fa-solid', icon: 'fa-file-chart-column' },
    [IconName.USERS]: { set: 'fa-solid', icon: 'fa-users' },
    [IconName.PROFILES]: { set: 'fa-solid', icon: 'fa-users' },
    [IconName.USER]: { set: 'fa-solid', icon: 'fa-user' },
    [IconName.ACCESSES]: { set: 'fa-solid', icon: 'fa-users' },
    [IconName.ATTRIBUTE]: { set: 'fa-solid', icon: 'fa-bars-progress' },
    [IconName.HOME]: { set: 'fa-solid', icon: 'fa-home' },
    [IconName.MAPS]: { set: 'fa-solid', icon: 'fa-map' },
    [IconName.SUPPORT]: { set: 'fa-solid', icon: 'fa-headset' },
    [IconName.INDICATORS_AND_OBJECTIVES]: { set: 'fa-solid', icon: 'fa-crosshairs' },
    [IconName.MANUAL_READINGS]: { set: 'fa-solid', icon: 'fa-gauge' },
    [IconName.IPMVP]: { set: 'fa-solid', icon: 'fa-chart-area' },
    [IconName.TYPICAL_WEEKS]: { set: 'fa-solid', icon: 'fa-calendar-days' },
    [IconName.TRACK]: { set: 'fa-light', icon: 'fa-file-chart-column' },
    [IconName.STEER]: { set: 'fa-light', icon: 'fa-chart-line-down' },
    [IconName.ANALYZE]: { set: 'fa-light', icon: 'fa-magnifying-glass-chart' },
    [IconName.NEW]: { set: 'fa-light', icon: 'fa-sparkles' },
    [IconName.PROFILE]: { set: 'fa-solid', icon: 'fa-address-card' },
    [IconName.LOG_IN]: { set: 'fa-solid', icon: 'fa-right-to-bracket' },
    [IconName.LOG_OUT]: { set: 'fa-solid', icon: 'fa-right-from-bracket' },
    [IconName.LOG_AS]: { set: 'fa-solid', icon: 'fa-user-secret' },
    [IconName.FEED]: { set: 'fa-solid', icon: 'fa-comments' },
    [IconName.PICTURE]: { set: 'fa-solid', icon: 'fa-camera' },
    [IconName.CANCEL]: { set: 'fa-solid', icon: 'fa-times' },
    [IconName.CLOSE_LEFT]: { set: 'fa-solid', icon: 'fa-chevrons-left' },
    [IconName.REFRESH]: { set: 'fa-solid', icon: 'fa-refresh' },
    [IconName.VALIDATE]: { set: 'fa-solid', icon: 'fa-check' },
    [IconName.FILTER]: { set: 'fa-solid', icon: 'fa-filter' },
    [IconName.SEARCH]: { set: 'fa-solid', icon: 'fa-search' },
    [IconName.COPY]: { set: 'fa-solid', icon: 'fa-copy' },
    [IconName.DUPLICATE]: { set: 'fa-solid', icon: 'fa-paste' },
    [IconName.FROM_TO]: { set: 'fa-solid', icon: 'fa-arrow-right' },
    [IconName.GO_TO_FEATURE]: { set: 'fa-solid', icon: 'fa-arrow-up-right' },
    [IconName.GO_TO]: { set: 'fa-solid', icon: 'fa-arrow-right' },
    [IconName.GO_BACK]: { set: 'fa-solid', icon: 'fa-arrow-left' },
    [IconName.PARENT]: { set: 'fa-solid', icon: 'fa-arrow-up' },
    [IconName.RELEASE_NOTES]: { set: 'fa-solid', icon: 'fa-file-alt' },
    [IconName.LOADING]: { set: 'fa-solid', icon: 'fa-refresh' },
    [IconName.EXPORT]: { set: 'fa-solid', icon: 'fa-download' },
    [IconName.IMPORT]: { set: 'fa-solid', icon: 'fa-upload' },
    [IconName.ANY_CHART]: { set: 'fa-solid', icon: 'fa-chart-mixed' },
    [IconName.COLUMN_CHART]: { set: 'fa-solid', icon: 'fa-chart-simple' },
    [IconName.LINE_CHART]: { set: 'fa-solid', icon: 'fa-chart-line' },
    [IconName.INDEX_CHART]: { set: 'fa-solid', icon: 'fa-display-chart-up' },
    [IconName.HEATMAP]: { set: 'fa-solid', icon: 'fa-sun-dust' },
    [IconName.COMBO_CHART]: { set: 'fa-solid', icon: 'fa-chart-mixed' },
    [IconName.PIE_CHART]: { set: 'fa-solid', icon: 'fa-pie-chart' },
    [IconName.BAR_CHART]: { set: 'fa-solid', icon: 'fa-chart-simple-horizontal' },
    [IconName.SINGLE_VALUE]: { set: 'fa-solid', icon: 'fa-hashtag' },
    [IconName.SCATTER_CHART]: { set: 'fa-solid', icon: 'fa-chart-scatter' },
    [IconName.TABLE]: { set: 'fa-solid', icon: 'fa-table-cells-large' },
    [IconName.PIVOT_TABLE]: { set: 'fa-solid', icon: 'fa-table-pivot' },
    [IconName.LEFT]: { set: 'fa-solid', icon: 'fa-arrow-left' },
    [IconName.RIGHT]: { set: 'fa-solid', icon: 'fa-arrow-right' },
    [IconName.SURFACE]: { set: 'fa-solid', icon: 'fa-ruler' },
    [IconName.INCREASE]: { set: 'fa-solid', icon: 'fa-arrow-up' },
    [IconName.DECREASE]: { set: 'fa-solid', icon: 'fa-arrow-down' },
    [IconName.STABLE]: { set: 'fa-solid', icon: 'fa-dash' },
    [IconName.QUICK_ACTION]: { set: 'fa-solid', icon: 'fa-arrows-to-dot' },
    [IconName.SHORTCUT]: { set: 'fa-solid', icon: 'fa-share-from-square' },
    [IconName.FUNCTION]: { set: 'fa-solid', icon: 'fa-function' },
    [IconName.TOP]: { set: 'fa-solid', icon: 'fa-trophy' },
    [IconName.VOTE]: { set: 'fa-solid', icon: 'fa-thumbs-up' }
};

const phosphorIconMap: Record<IconName, { icon: string; classes?: Record<string, boolean> } | { svg: string }> = {
    [IconName.CREATE]: { icon: 'ph-plus' },
    [IconName.ADD]: { icon: 'ph-plus' },
    [IconName.EDIT]: { icon: 'ph-pencil-simple-line' },
    [IconName.CONFIGURE]: { icon: 'ph-gear' },
    [IconName.DELETE]: { icon: 'ph-trash' },
    [IconName.SAVE]: { icon: 'ph-floppy-disk' },
    [IconName.CONVERT]: { icon: 'ph-arrows-down-up' },
    [IconName.OPEN]: { icon: 'ph-folder-open' },
    [IconName.REMOVE]: { icon: 'ph-x' },
    [IconName.DOWNLOAD]: { icon: 'ph-download-simple' },
    [IconName.DOWNLOADED]: { svg: 'downloaded' },
    [IconName.PRINT]: { icon: 'ph-printer' },
    [IconName.SELECT_FILE]: { icon: 'ph-folder-open' },
    [IconName.LIST]: { icon: 'ph-list-bullets' },
    [IconName.FOLDER]: { icon: 'ph-folder' },
    [IconName.HISTORY]: { icon: 'ph-clock-counter-clockwise' },
    [IconName.VISIBLE]: { icon: 'ph-eye' },
    [IconName.HIDDEN]: { icon: 'ph-eye-slash' },
    [IconName.LOCKED]: { icon: 'ph-lock-simple' },
    [IconName.UNLOCKED]: { icon: 'ph-lock-simple-open' },
    [IconName.NONE]: { icon: 'ph-empty' }, // TODO Remove this completely ? See later
    [IconName.YES]: { icon: 'ph-check-circle' },
    [IconName.EXPAND]: { icon: 'ph-caret-right', classes: { 'animate-transform': true, 'rot-0': true } },
    [IconName.RETRACT]: { icon: 'ph-caret-right', classes: { 'animate-transform': true, 'rot-90': true } },
    [IconName.EXPAND_SIDE]: { icon: 'ph-caret-down', classes: { 'animate-transform': true, 'rot-0': true } },
    [IconName.RETRACT_SIDE]: {
        icon: 'ph-caret-down',
        classes: { 'animate-transform': true, 'rot-minus90': true }
    },
    [IconName.PREVIOUS]: { icon: 'ph-caret-left' },
    [IconName.NEXT]: { icon: 'ph-caret-right' },
    [IconName.UP]: { icon: 'ph-arrow-up' },
    [IconName.DOWN]: { icon: 'ph-arrow-down' },
    [IconName.POSITIVE]: { icon: 'ph-smiley' },
    [IconName.NEUTRAL]: { icon: 'ph-smiley-meh' },
    [IconName.NEGATIVE]: { icon: 'ph-smiley-sad' },
    [IconName.SUCCESS]: { icon: 'ph-thumbs-up' },
    [IconName.FAILURE]: { icon: 'ph-thumbs-down' },
    [IconName.RATING_VERY_GOOD]: { icon: 'ph-seal-check' },
    [IconName.RATING_GOOD]: { icon: 'ph-seal-check' },
    [IconName.RATING_MEDIUM]: { icon: 'ph-seal-question' },
    [IconName.RATING_BAD]: { icon: 'ph-seal-warning' },
    [IconName.RATING_VERY_BAD]: { icon: 'ph-seal-warning' },
    [IconName.LATE]: { icon: 'ph-clock' },
    [IconName.FAVORITE]: { icon: 'ph-star' },
    [IconName.HELP]: { icon: 'ph-question' },
    [IconName.INFO]: { icon: 'ph-info' },
    [IconName.EXPORT_SPREADSHEET]: { icon: 'ph-download-simple' }, // TODO Should we remove this ?
    [IconName.DATE]: { icon: 'ph-calendar-blank' },
    [IconName.WARNING]: { icon: 'ph-warning' },
    [IconName.SEND_MESSAGE]: { icon: 'ph-envelope-simple' },
    [IconName.MENU]: { icon: 'ph-list' },
    [IconName.DROPDOWN_MENU]: { icon: 'ph-caret-down' },
    [IconName.PANEL_SELECTOR]: { icon: 'ph-dots-nine' },
    [IconName.MORE]: { svg: 'more' },
    [IconName.CALL]: { icon: 'ph-phone' },
    [IconName.SHARE]: { icon: 'ph-share' },
    [IconName.TARGET]: { icon: 'ph-target' },
    [IconName.AUTOMATIC]: { icon: 'ph-magic-wand' },
    [IconName.MISC_DATA]: { icon: 'ph-database' },
    [IconName.HOME_LIGHT]: { icon: 'ph-house' }, // TODO REMOVE THIS AFTER FEATURE FLAG REMOVAL
    [IconName.IMPORT_AND_EXPORT]: { icon: 'ph-download-simple' },
    [IconName.SUPPORT_LIGHT]: { icon: 'ph-headset' }, // TODO REMOVE THIS AFTER FEATURE FLAG REMOVAL
    [IconName.ADMIN]: { icon: 'ph-user-gear' },
    [IconName.BUILDING]: { icon: 'ph-building' },
    [IconName.BUILDINGS]: { icon: 'ph-building-office' },
    [IconName.CLASSIFICATION]: { icon: 'ph-tree-structure' },
    [IconName.DELIVERY_POINT]: { svg: 'delivery-point' },
    [IconName.SYSTEM]: { svg: 'system' },
    [IconName.WIRELESS]: { icon: 'ph-wifi-high' },
    [IconName.ALERT]: { icon: 'ph-bell' },
    [IconName.DASHBOARD]: { icon: 'ph-squares-four' },
    [IconName.METER]: { icon: 'ph-arrows-in-cardinal' },
    [IconName.ACTION]: { icon: 'ph-clipboard-text' },
    [IconName.INVOICE]: { icon: 'ph-invoice' },
    [IconName.SUPPLIER]: { icon: 'ph-plugs-connected' },
    [IconName.PROJECT]: { icon: 'ph-stack' },
    [IconName.REPORT]: { icon: 'ph-presentation-chart' },
    [IconName.USERS]: { icon: 'ph-users-three' },
    [IconName.PROFILES]: { icon: 'ph-identification-card' },
    [IconName.USER]: { icon: 'ph-user' },
    [IconName.ACCESSES]: { icon: 'ph-user-check' },
    [IconName.ATTRIBUTE]: { icon: 'ph-faders-horizontal' },
    [IconName.HOME]: { icon: 'ph-house' },
    [IconName.MAPS]: { icon: 'ph-map-trifold' },
    [IconName.SUPPORT]: { icon: 'ph-headset' },
    [IconName.INDICATORS_AND_OBJECTIVES]: { icon: 'ph-crosshair-simple' },
    [IconName.MANUAL_READINGS]: { icon: 'ph-gauge' },
    [IconName.IPMVP]: { icon: 'ph-blueprint' },
    [IconName.TYPICAL_WEEKS]: { icon: 'ph-calendar-dots' },
    [IconName.TRACK]: { svg: 'track' },
    [IconName.STEER]: { icon: 'ph-chart-line-down' },
    [IconName.ANALYZE]: { svg: 'analyze' },
    [IconName.NEW]: { icon: 'ph-sparkle' },
    [IconName.PROFILE]: { icon: 'ph-identification-card' },
    [IconName.LOG_IN]: { icon: 'ph-sign-in' },
    [IconName.LOG_OUT]: { icon: 'ph-sign-out' },
    [IconName.LOG_AS]: { icon: 'ph-detective' },
    [IconName.FEED]: { icon: 'ph-chats-circle' },
    [IconName.PICTURE]: { icon: 'ph-camera' },
    [IconName.CANCEL]: { icon: 'ph-x' },
    [IconName.CLOSE_LEFT]: { icon: 'ph-caret-double-left' },
    [IconName.REFRESH]: { icon: 'ph-arrows-clockwise' },
    [IconName.VALIDATE]: { icon: 'ph-check' },
    [IconName.FILTER]: { icon: 'ph-funnel-simple' },
    [IconName.SEARCH]: { icon: 'ph-magnifying-glass' },
    [IconName.COPY]: { icon: 'ph-copy-simple' },
    [IconName.DUPLICATE]: { icon: 'ph-copy-simple' },
    [IconName.FROM_TO]: { icon: 'ph-arrow-right' },
    [IconName.GO_TO_FEATURE]: { icon: 'ph-arrow-up-right' },
    [IconName.GO_TO]: { icon: 'ph-arrow-right' },
    [IconName.GO_BACK]: { icon: 'ph-arrow-left' },
    [IconName.PARENT]: { icon: 'ph-arrow-up' },
    [IconName.RELEASE_NOTES]: { icon: 'ph-file-text' },
    [IconName.LOADING]: { icon: 'ph-arrows-clockwise' },
    [IconName.EXPORT]: { icon: 'ph-download-simple' },
    [IconName.IMPORT]: { icon: 'ph-upload-simple' },
    [IconName.ANY_CHART]: { icon: 'ph-chart-line' },
    [IconName.COLUMN_CHART]: { icon: 'ph-chart-bar' },
    [IconName.LINE_CHART]: { icon: 'ph-chart-line' },
    [IconName.INDEX_CHART]: { icon: 'ph-arrow-line-up-right' },
    [IconName.HEATMAP]: { svg: 'heatmap' },
    [IconName.COMBO_CHART]: { icon: 'ph-chart-line-up' },
    [IconName.PIE_CHART]: { icon: 'ph-chart-pie' },
    [IconName.BAR_CHART]: { icon: 'ph-chart-bar-horizontal' },
    [IconName.SINGLE_VALUE]: { icon: 'ph-hash' },
    [IconName.SCATTER_CHART]: { icon: 'ph-chart-scatter' },
    [IconName.TABLE]: { icon: 'ph-grid-four' },
    [IconName.PIVOT_TABLE]: { svg: 'pivot-table' },
    [IconName.LEFT]: { icon: 'ph-arrow-left' },
    [IconName.RIGHT]: { icon: 'ph-arrow-right' },
    [IconName.SURFACE]: { icon: 'ph-ruler' },
    [IconName.INCREASE]: { icon: 'ph-arrow-up' },
    [IconName.DECREASE]: { icon: 'ph-arrow-down' },
    [IconName.STABLE]: { icon: 'ph-minus' },
    [IconName.QUICK_ACTION]: { icon: 'ph-arrows-in-cardinal' },
    [IconName.SHORTCUT]: { icon: 'ph-arrow-square-out' },
    [IconName.FUNCTION]: { icon: 'ph-function' },
    [IconName.TOP]: { icon: 'ph-trophy' },
    [IconName.VOTE]: { icon: 'ph-thumbs-up' }
};

const SIZE_CLASS_SUFIX = '-icon-size';

// Generate the icon css file using this script : scripts/generate-icons-css.js
@Component({
    styleUrl: './ui-icon.scss',
    selector: 'ui-icon',
    templateUrl: 'ui-icon.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, AngularSvgIconModule]
})
export class UiIconComponent {
    #renderer = inject(Renderer2);
    #hostElement = inject(ElementRef);
    #colorsService = inject(SharedUiColorsService);
    #disabledFeaturesService = inject(DisabledFeaturesService);

    name = input<IconName>();
    size = input<AdvizeoUiElementSize>();
    iconStyle = input<IconStyle>(IconStyle.REGULAR);

    rawColor = input<string | null>(null); // new prop that should replace all other color over time to work with palettes (as this is a building block for final components)

    onBackground = input<SharedUiBackgroundColorOptions>({ color: SharedUiBackgroundColorName.LIGHT });
    color = input<SharedUiColorName | null>(null);
    onColoredBackground = input<SharedUiColoredBackgroundColorOptions | null>(null);

    private combinedColorInputs = computed(() => ({
        rawColor: this.rawColor(),
        background: this.onBackground(),
        coloredBackground: this.onColoredBackground(),
        color: this.color()
    }));

    finalColor = toSignal(
        toObservable(this.combinedColorInputs).pipe(
            switchMap(({ rawColor, background, color, coloredBackground }) => {
                if (rawColor) {
                    return of(rawColor);
                } else if (!color && !coloredBackground) {
                    return this.#colorsService.getTextColor$(SharedUiTextColorDerivation.MAIN, background);
                } else if (coloredBackground) {
                    return this.#colorsService.getTextColorOnColoredBackground$(coloredBackground);
                } else if (color === SharedUiColorName.PRIMARY) {
                    return this.#colorsService.getColor$(SharedUiColorName.PRIMARY);
                } else if (color) {
                    return this.#colorsService.getColoredTextColor$(color, background);
                } else {
                    return 'white';
                }
            })
        )
    );

    isSpinning = computed(() => this.name() === IconName.LOADING);

    classes = computed(() => {
        const name = this.name();
        if (!name) {
            return {};
        }

        if (this.#disabledFeaturesService.isFeatureEnabled(FeatureCode.PHOSPHOR_ICONS)) {
            const iconDescription = phosphorIconMap[name];

            if (!iconDescription) {
                console.error(`unknown icon used in ui-icon: ${name}`);
                return {};
            }

            if ('svg' in iconDescription) {
                return {};
            }

            return {
                [this.iconStyle() === IconStyle.REGULAR ? 'ph' : `ph-${this.iconStyle()}`]: true,
                [iconDescription.icon]: true,
                ...(iconDescription.classes ?? {})
            };
        } else {
            let iconDescription = fontAwesomeIconMap[name];

            if (!iconDescription) {
                console.error(`unknown icon used in ui-icon: ${name}`);
                return {};
            }

            if (name == 'favorite' && this.iconStyle() === 'regular') {
                iconDescription = { set: 'fa-regular', icon: 'fa-star' };
            }

            return {
                [iconDescription.set ?? '']: true,
                [iconDescription.icon ?? '']: true,
                ...(iconDescription.classes ?? {})
            };
        }
    });

    svg = computed<string | null>(() => {
        if (this.#disabledFeaturesService.isFeatureDisabled(FeatureCode.PHOSPHOR_ICONS)) {
            return null;
        }

        const name = this.name();
        if (!name) {
            return null;
        }
        const iconDescription = phosphorIconMap[name];

        if (!iconDescription) {
            console.error(`unknown icon used in ui-icon: ${name}`);
            return null;
        }

        if (!('svg' in iconDescription)) {
            return null;
        }
        return `ui/icon/assets/${iconDescription.svg}/${this.iconStyle()}.svg`;
    });

    constructor() {
        effect(() => {
            const size = this.size() || AdvizeoUiElementSize.MEDIUM;
            this.setSizeClassToHost(size);
        });
    }

    private setSizeClassToHost(size: AdvizeoUiElementSize) {
        Object.values(AdvizeoUiElementSize).forEach(s => {
            this.#renderer.removeClass(this.#hostElement.nativeElement, s + SIZE_CLASS_SUFIX);
        });
        this.#renderer.addClass(this.#hostElement.nativeElement, size + SIZE_CLASS_SUFIX);
    }
}
